import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Sidebar } from "../components/layout/Sidebar";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { 
  Building2, 
  Plus, 
  Trash2, 
  ChevronRight,
  Crown,
  CheckCircle,
  XCircle,
  AlertTriangle
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const formatCurrency = (value) => {
  if (!value) return "-";
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value);
};

const formatDate = (date) => {
  return new Date(date).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
};

// Grade badge colors
const gradeColors = {
  A: "bg-emerald-500/20 text-[#00FF88] drop-shadow-[0_0_10px_rgba(0,255,136,0.5)] border-emerald-500/50",
  B: "bg-blue-500/20 text-[#0A84FF] drop-shadow-[0_0_8px_rgba(10,132,255,0.4)] border-blue-500/50",
  C: "bg-amber-500/20 text-[#FF9F0A] drop-shadow-[0_0_8px_rgba(255,159,10,0.5)] border-amber-500/50",
  D: "bg-red-500/20 text-[#FF3B30] border-red-500/50"
};

// Decision badge config
const decisionConfig = {
  proceed: { color: "text-[#00FF88] drop-shadow-[0_0_10px_rgba(0,255,136,0.5)]", icon: CheckCircle, text: "Proceed" },
  renegotiate: { color: "text-[#FF9F0A] drop-shadow-[0_0_8px_rgba(255,159,10,0.5)]", icon: AlertTriangle, text: "Renegotiate" },
  reject: { color: "text-[#FF3B30]", icon: XCircle, text: "Reject" }
};

export default function UnderwritingListPage({ user, subscription, onLogout }) {
  const [analyses, setAnalyses] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const tier = subscription?.tier || "starter";

  useEffect(() => {
    fetchAnalyses();
  }, []);

  const fetchAnalyses = async () => {
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/underwriting/analyses`,
        { withCredentials: true }
      );
      setAnalyses(response.data);
    } catch (error) {
      console.error("Failed to fetch analyses:", error);
    } finally {
      setLoading(false);
    }
  };

  const deleteAnalysis = async (analysisId) => {
    if (!confirm("Are you sure you want to delete this analysis?")) return;
    
    try {
      await axios.delete(
        `${BACKEND_URL}/api/underwriting/analyses/${analysisId}`,
        { withCredentials: true }
      );
      setAnalyses(analyses.filter(a => a.analysis_id !== analysisId));
      toast.success("Analysis deleted");
    } catch (error) {
      toast.error("Failed to delete analysis");
    }
  };

  // Feature gate removed - all active subscribers have access
  return (
    <div className="min-h-screen bg-black flex">
      <Sidebar user={user} subscription={subscription} onLogout={onLogout} />
      
      <main className="ml-64 flex-1 p-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-heading text-2xl font-bold text-white uppercase">
              Multifamily Underwriting
            </h1>
            <p className="text-white/40 mt-1 text-sm">
              Institutional-grade analysis for evaluating deal profitability and risk.
            </p>
          </div>
          <Link to="/underwriting/new">
            <Button className="gold-gradient-btn rounded-none font-bold">
              <Plus className="w-5 h-5 mr-2" />
              New Analysis
            </Button>
          </Link>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="w-10 h-10 border-4 border-gold border-t-transparent rounded-full animate-spin" />
          </div>
        ) : analyses.length === 0 ? (
          <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.08] p-12 text-center">
            <Building2 className="w-16 h-16 text-white/40 mx-auto mb-4" />
            <h3 className="text-xl font-heading font-bold text-white mb-3">
              Analyze Before You Commit Capital
            </h3>
            <p className="text-white/50 mb-6 max-w-md mx-auto">
              Estimate profit spread, risk score, and exit confidence using institutional-grade underwriting tools. No more spreadsheets.
            </p>
            <Link to="/underwriting/new">
              <Button className="gold-gradient-btn rounded-none font-bold">
                <Plus className="w-5 h-5 mr-2" />
                Create New Analysis
              </Button>
            </Link>
          </div>
        ) : (
          <div className="space-y-4">
            {analyses.map((analysis) => {
              const decision = decisionConfig[analysis.results?.decision?.decision] || decisionConfig.reject;
              const DecisionIcon = decision.icon;
              const irr = analysis.results?.investor_metrics?.irr;
              const spread = analysis.results?.investor_metrics?.profit_on_sale;
              const riskLevel = analysis.results?.risk_assessment?.risk_level;
              
              return (
                <div 
                  key={analysis.analysis_id}
                  className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.08] p-6 hover:border-gold/50 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_15px_30px_rgba(0,0,0,0.3)]"
                >
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex flex-wrap items-center gap-3 mb-2">
                        <h3 className="font-heading text-lg font-bold text-white">
                          {analysis.name || "Untitled Analysis"}
                        </h3>
                        
                        {/* Grade Badge */}
                        <span className={`px-2 py-1 text-xs font-bold border ${
                          gradeColors[analysis.results?.risk_assessment?.grade] || gradeColors.D
                        }`}>
                          Grade {analysis.results?.risk_assessment?.grade || "?"}
                        </span>
                        
                        {/* Risk Level */}
                        {riskLevel && (
                          <span className="text-xs text-white/40 px-2 py-1 bg-white/[0.04] border border-white/[0.06]">
                            Risk: {riskLevel}
                          </span>
                        )}
                        
                        {/* Decision */}
                        <span className={`flex items-center gap-1 text-sm ${decision.color}`}>
                          <DecisionIcon className="w-4 h-4" />
                          {decision.text}
                        </span>
                      </div>
                      
                      <div className="flex flex-wrap items-center gap-4 text-sm">
                        <span className="text-white/50">{analysis.inputs?.property_overview?.address || "No address"}</span>
                        <span className="text-white/30">|</span>
                        <span className="text-white/50">{formatCurrency(analysis.inputs?.acquisition?.purchase_price)}</span>
                        {irr != null && (
                          <>
                            <span className="text-white/30">|</span>
                            <span className={`font-mono font-semibold ${irr >= 15 ? 'text-[#00FF88]' : irr >= 12 ? 'text-white' : 'text-[#FF3B30]'}`}>
                              IRR: {irr.toFixed(1)}%
                            </span>
                          </>
                        )}
                        {spread != null && (
                          <>
                            <span className="text-white/30">|</span>
                            <span className={`font-mono font-semibold ${spread > 0 ? 'text-[#00FF88]' : 'text-[#FF3B30]'}`}>
                              Spread: {formatCurrency(spread)}
                            </span>
                          </>
                        )}
                        <span className="text-white/30">|</span>
                        <span className="text-white/40 text-xs">{formatDate(analysis.updated_at || analysis.created_at)}</span>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <Link to={`/underwriting/${analysis.analysis_id}`}>
                        <Button className="gold-gradient-btn rounded-none">
                          View Analysis
                          <ChevronRight className="w-4 h-4 ml-1" />
                        </Button>
                      </Link>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => deleteAnalysis(analysis.analysis_id)}
                        className="text-[#FF3B30] hover:text-red-300 hover:bg-red-500/10"
                      >
                        <Trash2 className="w-5 h-5" />
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
}
